/**
 * Entity Types query hooks
 */

import { useQuery } from '@tanstack/react-query';
import { entityTypeApi } from '@/lib/api/entity-types.api';
import { systemPreferenceApi } from '@/lib/api/system-preferences.api';
import { queryKeys } from '../query-keys';
import { useOrganizationStore } from '@/store/organization.store';
import { type EntityType } from '@scrambl/shared';

const ENTITY_SETTINGS_KEY = 'entity.settings';

interface EntitySettings {
  enableEntityTypes: boolean;
  requireWebsite: boolean;
  requireIndustry: boolean;
  autoEnrichData: boolean;
  duplicateCheckEnabled: boolean;
  duplicateCheckField: string;
  enableRelationships: boolean;
  enableActivityTracking: boolean;
}

const DEFAULT_ENTITY_SETTINGS: EntitySettings = {
  enableEntityTypes: false,
  requireWebsite: false,
  requireIndustry: false,
  autoEnrichData: false,
  duplicateCheckEnabled: true,
  duplicateCheckField: 'name',
  enableRelationships: true,
  enableActivityTracking: true,
};

const normalizeEntitySettings = (settings: EntitySettings): EntitySettings => {
  const duplicateCheckField = settings.duplicateCheckField === 'name'
    ? settings.duplicateCheckField
    : DEFAULT_ENTITY_SETTINGS.duplicateCheckField;

  return {
    ...settings,
    duplicateCheckField,
  };
};

/**
 * Hook to fetch all entity types for an organization
 */
export function useEntityTypesQuery() {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<EntityType[]>({
    queryKey: queryKeys.entityTypes.list(),
    queryFn: () => entityTypeApi.getAll(currentOrganization!.id),
    enabled: !!currentOrganization?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch visible (non-hidden) entity types
 */
export function useVisibleEntityTypesQuery() {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<EntityType[]>({
    queryKey: queryKeys.entityTypes.visible(),
    queryFn: () => entityTypeApi.getVisible(currentOrganization!.id),
    enabled: !!currentOrganization?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a single entity type by ID
 */
export function useEntityTypeQuery(id: string) {
  return useQuery<EntityType>({
    queryKey: queryKeys.entityTypes.detail(id),
    queryFn: () => entityTypeApi.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch the default entity type
 */
export function useDefaultEntityTypeQuery() {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<EntityType | null>({
    queryKey: queryKeys.entityTypes.default(),
    queryFn: () => entityTypeApi.getDefault(currentOrganization!.id),
    enabled: !!currentOrganization?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch entity counts by type
 */
export function useEntityTypeCountsQuery() {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<Record<string, number>>({
    queryKey: queryKeys.entityTypes.counts(),
    queryFn: () => entityTypeApi.getEntityCounts(currentOrganization!.id),
    enabled: !!currentOrganization?.id,
    staleTime: 60 * 1000, // 1 minute - counts change more frequently
  });
}

/**
 * Hook to fetch entity module settings
 * Returns the enableEntityTypes flag and other settings
 */
export function useEntitySettingsQuery() {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<EntitySettings>({
    queryKey: ['entitySettings', currentOrganization?.id],
    queryFn: async () => {
      const savedSettings = await systemPreferenceApi.getPreference(ENTITY_SETTINGS_KEY);
      if (savedSettings?.customValue) {
        return normalizeEntitySettings({
          ...DEFAULT_ENTITY_SETTINGS,
          ...(savedSettings.customValue as Partial<EntitySettings>),
        });
      }
      return DEFAULT_ENTITY_SETTINGS;
    },
    enabled: !!currentOrganization?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Entity types are deprecated and globally disabled. The option is hidden in
 * settings and the feature is force-disabled everywhere it's consumed, which
 * auto-disables it for existing orgs that previously had it enabled. Existing
 * type data is preserved (not deleted), so this can be reverted if needed.
 */
const ENTITY_TYPES_DISABLED = true;

/**
 * Hook to check if entity types feature is enabled
 * Returns a simple boolean for easy consumption
 */
export function useEntityTypesEnabled(): boolean {
  const { data: settings, isLoading } = useEntitySettingsQuery();
  if (ENTITY_TYPES_DISABLED) {
    return false;
  }
  // Default to false while loading to avoid flash of content
  if (isLoading || !settings) {
    return false;
  }
  return settings.enableEntityTypes;
}
